import type { ReactNode } from 'react'
import { Badge, Button, type BadgeTone } from '@highlight/ui'

export function formatPlannerDebugDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))
}

export type PlannerDebugTone = 'neutral' | 'good' | 'warn' | 'bad' | 'info'

export const PLANNER_DEBUG_TONE_TEXT: Record<PlannerDebugTone, string> = {
  neutral: 'text-foreground-secondary',
  good: 'text-success-subtle',
  warn: 'text-yellow-11',
  bad: 'text-danger-subtle',
  info: 'text-blue-11',
}

const PLANNER_DEBUG_BADGE_TONE: Record<PlannerDebugTone, BadgeTone> = {
  neutral: 'neutral',
  good: 'success',
  warn: 'warning',
  bad: 'danger',
  info: 'info',
}

export function PlannerDebugBadge({ children, tone = 'neutral' }: { children: ReactNode; tone?: PlannerDebugTone }) {
  return (
    <Badge size="sm" tone={PLANNER_DEBUG_BADGE_TONE[tone]} className="uppercase tracking-wide">
      {children}
    </Badge>
  )
}

/** Small uppercase heading over one block of a run's detail. */
export function PlannerDebugHeading({ children }: { children: ReactNode }) {
  return <h4 className="text-foreground-secondary type-ui-xs m-0 font-medium uppercase tracking-wide">{children}</h4>
}

export function PlannerDebugError({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div className="border-danger corner-smooth flex flex-col items-start gap-2 rounded-lg border p-3">
      <p className="text-danger-subtle type-ui-xs">
        {error instanceof Error ? error.message : 'Something went wrong.'}
      </p>
      {retry && (
        <Button variant="outline" onClick={retry}>
          Retry
        </Button>
      )}
    </div>
  )
}

export function PlannerDebugJson({ label, value }: { label: string; value: unknown }) {
  if (value == null) return null
  return (
    <details>
      <summary className="text-foreground-secondary hover:text-foreground type-ui-xs cursor-pointer">{label}</summary>
      <pre className="bg-background/60 text-foreground corner-smooth type-code-xs mt-1 max-h-80 overflow-auto rounded-lg border p-2">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  )
}
